import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Sparkles, AlertTriangle, ShieldCheck, Loader2, Users } from "lucide-react";
import { SectionHeader, RatingBadge, RiskFlagBadge } from "@/components/common/Widgets";
import { fmtNum } from "@/lib/format";

const RETAIL_LOAN_TYPES = ["PERSONAL_LOAN", "HOUSING_LOAN"];

export default function CRASection({ app, cma, cra, customer, reload }) {
  const isRetail = RETAIL_LOAN_TYPES.includes((app?.loan_type || "").toUpperCase());
  const [businessType, setBusinessType] = useState(cra?.business_type || "trading");
  const [conduct, setConduct] = useState((cra?.result?.scores?.conduct_of_account?.breakdown?.conduct) || "Satisfactory");
  const [industryOutlook, setIndustryOutlook] = useState(8);
  const [locationScore, setLocationScore] = useState(7);
  const [competitionScore, setCompetitionScore] = useState(5);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    // Business CRA works without CMA but with zero financial scores — show a soft warning instead of blocking.
    if (!isRetail && !cma?.balance_sheet) {
      toast.warning("CMA not saved — financial scores will default to zero. Save CMA financials for an accurate rating.", { duration: 5000 });
    }
    setBusy(true);
    try {
      const res = await api.post("/cra/run", {
        application_id: app.id,
        business_type: businessType,
        qualitative: { conduct, industry_outlook_score: Number(industryOutlook), location_score: Number(locationScore), competition_score: Number(competitionScore) },
      });
      const rating = res.data?.result?.sb_rating || "Done";
      if (res.data?.result?.cma_missing) {
        toast.info(`CRA computed: ${rating} (approximate — CMA missing)`, { duration: 5000 });
      } else {
        toast.success(`CRA computed: ${rating}`);
      }
      reload && reload();
    } catch (err) {
      let msg = "CRA failed";
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) msg = detail.map((d) => `${(d.loc || []).slice(-1)[0] || "field"}: ${d.msg}`).join("; ");
      else if (typeof detail === "string") msg = detail;
      else if (err?.message) msg = err.message;
      // eslint-disable-next-line no-console
      console.error("CRA error:", err);
      toast.error(msg, { duration: 6000 });
    } finally { setBusy(false); }
  };

  const r = cra?.result;
  const mode = r?.mode || (isRetail ? "retail" : "business");

  return (
    <div className="space-y-4">
      <SectionHeader
        title="CRA Engine (Credit Risk Assessment)"
        subtitle={isRetail
          ? "Computes SB1–SB10 from bureau scores, FOIR and employer profile (retail mode)."
          : "Computes SB1–SB10 from financial ratios + qualitative inputs (business mode)."}
      />

      {/* Mode banner */}
      <div className={`rounded-md border p-3 text-xs flex items-center gap-2 ${isRetail ? "border-accent/30 bg-accent/5 text-accent" : "border-primary/20 bg-primary/5"}`}>
        <ShieldCheck className="h-4 w-4" />
        <span className="font-medium">{isRetail ? "Retail / Individual Loan" : "Business / Working-Capital Loan"}</span>
        <Badge variant="outline" className="text-[10px]">{(app?.loan_type || "").toUpperCase()}</Badge>
      </div>

      {/* CMA-missing helper (business mode only) */}
      {!isRetail && !cma?.balance_sheet && (
        <div
          className="rounded-md border border-warning/30 bg-warning/5 text-warning p-3 text-xs flex items-start gap-2"
          data-testid="cra-cma-missing-banner"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">CMA not saved yet</div>
            <div className="text-warning/80 mt-0.5">
              You can still run CRA scoring, but the <span className="font-semibold">Financial Risk</span> bucket (40 pts) will default to zero. For an accurate rating, please save the CMA financials first.
            </div>
          </div>
        </div>
      )}

      {!isRetail && (
        <Card className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <FieldRow label="Business Type">
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger data-testid="cra-business-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trading">Trading</SelectItem>
                  <SelectItem value="non-trading">Non-Trading (Manufacturing/Services)</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Conduct of Account">
              <Select value={conduct} onValueChange={setConduct}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Satisfactory", "Average", "Irregular"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Industry Outlook (0–10)"><Input type="number" min={0} max={10} value={industryOutlook} onChange={(e) => setIndustryOutlook(e.target.value)} /></FieldRow>
            <FieldRow label="Location Score (0–8)"><Input type="number" min={0} max={8} value={locationScore} onChange={(e) => setLocationScore(e.target.value)} /></FieldRow>
            <FieldRow label="Competition (0–7)"><Input type="number" min={0} max={7} value={competitionScore} onChange={(e) => setCompetitionScore(e.target.value)} /></FieldRow>
            <div className="md:col-span-3 flex items-end justify-end">
              <Button onClick={run} disabled={busy} data-testid="cra-run-button"><Sparkles className="h-4 w-4 mr-1" />{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run CRA Scoring"}</Button>
            </div>
          </div>
        </Card>
      )}

      {isRetail && (
        <Card className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Snippet k="Loan Amount" v={`₹ ${(app?.personal_loan_amount || app?.housing_loan_amount || 0).toFixed(2)} L`} />
            <Snippet k="Tenor" v={`${app?.tenor_months || 0} months`} />
            <Snippet k="Interest Rate" v={`${app?.interest_rate || 0}% p.a.`} />
            <Snippet k="Net Monthly Income" v={`₹ ${(app?.net_monthly_income || 0).toLocaleString("en-IN")}`} />
            <Snippet k="Existing EMI" v={`₹ ${(app?.existing_emi || 0).toLocaleString("en-IN")}`} />
            <Snippet k="FOIR" v={app?.foir_pct ? `${app.foir_pct}%` : "—"} />
            <Snippet k="Employer Type" v={app?.employer_type || "—"} />
            <Snippet k="Co-applicants" v={(app?.co_applicants || []).length} />
          </div>
          <div className="flex justify-end mt-3 pt-3 border-t border-border">
            <Button onClick={run} disabled={busy} data-testid="cra-run-button"><Sparkles className="h-4 w-4 mr-1" />{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run CRA Scoring"}</Button>
          </div>
        </Card>
      )}

      {r && (
        <>
          <Card className="p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <RatingBadge rating={r.sb_rating} />
                <Badge variant="outline" className="text-[10px]">{mode.toUpperCase()} MODE</Badge>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Decision</div>
                  <div className="text-base font-semibold">{r.decision_recommendation}</div>
                </div>
                <div className="hidden md:block h-8 w-px bg-border" />
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Pricing Slab</div>
                  <div className="text-base font-semibold mono">{r.pricing_slab}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-3xl font-semibold mono tabular">{r.total_score}<span className="text-base text-muted-foreground">/{r.max_score}</span></div>
                <ShieldCheck className="h-7 w-7 text-success" />
              </div>
            </div>
            <div className="mt-3"><Progress value={r.total_score} /></div>
          </Card>

          {/* Score cards — render whatever keys are present (works for both modes) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {Object.entries(r.scores || {}).map(([key, val]) => (
              <ScoreCard
                key={key}
                title={prettyTitle(key)}
                max={val?.max ?? 0}
                score={val?.score ?? 0}
                drivers={flattenBreakdown(val?.breakdown)}
              />
            ))}
          </div>

          <Card className="p-4">
            <SectionHeader title="Risk Flags" subtitle={`${(r.risk_flags || []).length} flag(s) raised`} />
            {(!r.risk_flags || r.risk_flags.length === 0) ? (
              <div className="text-sm text-success flex items-center gap-2"><ShieldCheck className="h-4 w-4" />No flags — all key metrics within benchmarks.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {r.risk_flags.map((f, i) => <RiskFlagBadge key={i} sev={f.sev} msg={f.msg} />)}
              </div>
            )}
            <div className="mt-3 text-xs text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5 inline-block mr-1" />
              Benchmarks: {mode === "retail"
                ? "CIBIL ≥700 · FOIR ≤50% · LTV ≤90% (housing)"
                : "CR ≥1.33 · D/E ≤2.0 · DSCR ≥1.5 · ICR ≥2.0 · CIBIL ≥700"}
            </div>
          </Card>
        </>
      )}

      {!r && (
        <Card className="p-8 text-center border-dashed">
          <Sparkles className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Click "Run CRA Scoring" to compute the rating.</p>
        </Card>
      )}
    </div>
  );
}

function Snippet({ k, v }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className="text-sm font-medium mt-0.5 mono">{v}</div>
    </div>
  );
}

function prettyTitle(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function flattenBreakdown(breakdown) {
  if (!breakdown || typeof breakdown !== "object") return [];
  return Object.entries(breakdown).map(([k, v]) => {
    if (v && typeof v === "object" && "score" in v) {
      return { k, v: v.value, score: v.score, max: v.max };
    }
    return { k, v };
  });
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function ScoreCard({ title, score, max, drivers }) {
  const pct = max ? (score / max) * 100 : 0;
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold heading">{title}</div>
        <div className="mono text-sm font-semibold">{score}/{max}</div>
      </div>
      <Progress value={pct} className="mt-2 h-1.5" />
      <ul className="mt-3 space-y-1.5 text-xs">
        {drivers.map((d, i) => (
          <li key={i} className="flex justify-between gap-2">
            <span className="text-muted-foreground capitalize">{d.k.replace(/_/g, " ")}</span>
            <span className="mono">
              {typeof d.v === "number" ? fmtNum(d.v, 2) : String(d.v ?? "—")}
              {d.score !== undefined ? ` · ${d.score}/${d.max}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
